import { defineStore } from 'pinia';
import Config from '@/config';
import { AuthenticationApiClient } from '@cyber-range/cyber-range-api-authentication-client';
import { ApiRequestConfig, IApiClientError } from '@cyber-range/cyber-range-api-client';
import { TrackJS } from 'trackjs';
import { UserApiClient, IUserApiClient } from '@cyber-range/cyber-range-api-user-client';
import { InvitationApiClient, IInvitationApiClient } from '@cyber-range/cyber-range-api-invitation-client';
import { IEntitlementApiClient, EntitlementApiClient } from '@cyber-range/cyber-range-api-entitlement-client';
import { ICompetitionApiClient, CompetitionApiClient } from '@cyber-range/cyber-range-api-ctf-competition-client';
import { IPaymentApiClient, IVendibleApiClient, PaymentApiClient, VendibleApiClient } from '@cyber-range/cyber-range-api-payment-client'
import { IBalanceApiClient, BalanceApiClient } from '@cyber-range/cyber-range-api-balance-client'
import { IContentApiClient, ContentApiClient }from '@cyber-range/cyber-range-api-content-client';
import { IBusinessUnitApiClient, BusinessUnitApiClient } from '@cyber-range/cyber-range-api-business-unit-client';
import { IOrganizationApiClient, OrganizationApiClient } from '@cyber-range/cyber-range-api-organization-client';
import { NewsletterApiClient } from '@cyber-range/cyber-range-api-newsletter-client'
import { CatalogApiClient, CatalogFamilyAccessApiClient, ICatalogApiClient, ICatalogFamilyAccessApiClient } from '@cyber-range/cyber-range-api-catalog-client';
import { useErrorStore } from './errorStore';
import { useAuthenticationStore } from './authenticationStore';

function customApiErrorHandler(e: IApiClientError) {
	TrackJS.track(e);
	useErrorStore().setError(e);
}

function apiCallingHandler() {
	useApiClientStore().loadingBegin();
}

function apiCalledHandler() {
	useApiClientStore().loadingEnd();
}

export const useApiClientStore = defineStore('apiClient', {
	state: () => ({
		loading: 0
	}),
	getters: {
		isLoading(): boolean {
			return this.loading > 0;
		},
		authenticationApiClient(): AuthenticationApiClient {
			const config = new ApiRequestConfig(
				{ "authorization": `Bearer ${useAuthenticationStore().token}`, 'User-Agent': undefined },
				undefined,
				customApiErrorHandler,
				apiCallingHandler,
				apiCalledHandler
			);
			return new AuthenticationApiClient(Config.AUTHENTICATION_API_BASE_URL, config);
		},
		backgroundAuthenticationApiClient(): AuthenticationApiClient {
			const config = new ApiRequestConfig(
				{ "authorization": `Bearer ${useAuthenticationStore().token}`, 'User-Agent': undefined },
				undefined,
				undefined,
				undefined,
				undefined
			);
			return new AuthenticationApiClient(Config.AUTHENTICATION_API_BASE_URL, config);
		},
		entitlementApiClient(): IEntitlementApiClient {
			const config = new ApiRequestConfig(
				{ "authorization": `Bearer ${useAuthenticationStore().token}`, 'User-Agent': undefined },
				undefined,
				customApiErrorHandler,
				apiCallingHandler,
				apiCalledHandler
			);
			return new EntitlementApiClient(Config.ENTITLEMENT_API_BASE_URL, config);
		},
		userApiClient(): IUserApiClient {
			const config = new ApiRequestConfig(
				{ "authorization": `Bearer ${useAuthenticationStore().token}`, 'User-Agent': undefined },
				undefined,
				customApiErrorHandler,
				apiCallingHandler,
				apiCalledHandler
			);
			return new UserApiClient(Config.USER_API_BASE_URL, config);
		},
		backgroundUserApiClient(): IUserApiClient {
			const config = new ApiRequestConfig(
				{ "authorization": `Bearer ${useAuthenticationStore().token}`, 'User-Agent': undefined },
			);
			return new UserApiClient(Config.USER_API_BASE_URL, config);
		},
		invitationApiClient(): IInvitationApiClient {
			const config = new ApiRequestConfig(
				{ "authorization": `Bearer ${useAuthenticationStore().token}`, 'User-Agent': undefined },
				undefined,
				customApiErrorHandler,
				apiCallingHandler,
				apiCalledHandler
			);
			return new InvitationApiClient(Config.INVITATION_API_BASE_URL, config);
		},
		competitionApiClient(): ICompetitionApiClient {
			const config = new ApiRequestConfig(
				{ "authorization": `Bearer ${useAuthenticationStore().token}`, 'User-Agent': undefined },
				undefined,
				customApiErrorHandler,
				apiCallingHandler,
				apiCalledHandler
			);
			return new CompetitionApiClient(Config.COMPETITION_API_BASE_URL, config);
		},
		paymentApiClient(): IPaymentApiClient {
			const config = new ApiRequestConfig(
				{ "authorization": `Bearer ${useAuthenticationStore().token}`, 'User-Agent': undefined },
				undefined,
				customApiErrorHandler,
				apiCallingHandler,
				apiCalledHandler
			);
			return new PaymentApiClient(Config.PAYMENT_API_BASE_URL, config);
		},
		vendibleApiClient(): IVendibleApiClient {
			const config = new ApiRequestConfig(
				{ "authorization": `Bearer ${useAuthenticationStore().token}`, 'User-Agent': undefined },
				undefined,
				customApiErrorHandler,
				apiCallingHandler,
				apiCalledHandler
			);
			return new VendibleApiClient(Config.PAYMENT_API_BASE_URL, config);
		},
		balanceApiClient(): IBalanceApiClient {
			const config = new ApiRequestConfig(
				{ "authorization": `Bearer ${useAuthenticationStore().token}`, 'User-Agent': undefined },
				undefined,
				customApiErrorHandler,
				apiCallingHandler,
				apiCalledHandler
			);
			return new BalanceApiClient(Config.BALANCE_API_BASE_URL, config);
		},
		contentApiClient(): IContentApiClient {
			const config = new ApiRequestConfig(
				{ "authorization": `Bearer ${useAuthenticationStore().token}`, 'User-Agent': undefined },
				undefined,
				customApiErrorHandler,
				apiCallingHandler,
				apiCalledHandler
			);
			return new ContentApiClient(Config.CONTENT_API_BASE_URL, config);
		},
		businessUnitApiClient(): IBusinessUnitApiClient {
			const config = new ApiRequestConfig(
				{ "authorization": `Bearer ${useAuthenticationStore().token}`, 'User-Agent': undefined },
				undefined,
				customApiErrorHandler,
				apiCallingHandler,
				apiCalledHandler
			);
			return new BusinessUnitApiClient(Config.BUSINESS_UNIT_API_BASE_URL, config);
		},
		organizationApiClient(): IOrganizationApiClient {
			const config = new ApiRequestConfig(
				{ "authorization": `Bearer ${useAuthenticationStore().token}`, 'User-Agent': undefined },
				undefined,
				customApiErrorHandler,
				apiCallingHandler,
				apiCalledHandler
			);
			return new OrganizationApiClient(Config.ORGANIZATION_API_BASE_URL, config);
		},
		backgroundOrganizationApiClient(): IOrganizationApiClient {
			const config = new ApiRequestConfig(
				{ "authorization": `Bearer ${useAuthenticationStore().token}`, 'User-Agent': undefined },
			);
			return new OrganizationApiClient(Config.ORGANIZATION_API_BASE_URL, config);
		},
		newsletterApiClient(): NewsletterApiClient {
			const config = new ApiRequestConfig(
				{ "authorization": `Bearer ${useAuthenticationStore().token}`, 'User-Agent': undefined },
				undefined,
				customApiErrorHandler,
				apiCallingHandler,
				apiCalledHandler
			);
			return new NewsletterApiClient(Config.NEWSLETTER_API_BASE_URL, config);
		},
		catalogFamilyAccessApiClient(): ICatalogFamilyAccessApiClient {
			const config = new ApiRequestConfig(
				{ "authorization": `Bearer ${useAuthenticationStore().token}`, 'User-Agent': undefined },
				undefined,
				customApiErrorHandler,
				apiCallingHandler,
				apiCalledHandler
			);
			return new CatalogFamilyAccessApiClient(Config.CATALOG_API_BASE_URL, config);
		},
		catalogApiClient(): ICatalogApiClient {
			const config = new ApiRequestConfig(
				{ "authorization": `Bearer ${useAuthenticationStore().token}`, 'User-Agent': undefined },
				undefined,
				customApiErrorHandler,
				apiCallingHandler,
				apiCalledHandler
			);
			return new CatalogApiClient(Config.CATALOG_API_BASE_URL, config);
		}
	},
	actions: {
		loadingBegin(): number {
			return ++this.loading;
		},
		loadingEnd(): number {
			return --this.loading;
		}
	}
});
